/**
 * The IndexedDB catalogue store. No wrapper library — `idb` is 4 KB gzipped for an API this
 * file uses six times, and the POS has a 2-second budget on a mid-range Android over 3G.
 *
 * One database, two stores: the variants themselves, and a tiny `meta` store holding the sync
 * watermark. The watermark is what turns the daily refresh from a megabyte into a delta.
 */

import type { CatalogueVariant, ShopId, VariantId } from '@pos/shared';

const DB_NAME = 'pos-catalogue';
const DB_VERSION = 1;
const VARIANTS = 'variants';
const META = 'meta';

export type SyncMeta = {
  /** Server timestamp of the newest row we hold. Sent as `since` on the next sync. */
  watermark: string;
  /** When the client last completed a sync, for the "last updated" line in the UI. */
  syncedAt: string;
  /** The catalogue is per shop — switching shops must not search another shop's stock. */
  shopId: ShopId;
  count: number;
};

let handle: Promise<IDBDatabase> | null = null;

function open(): Promise<IDBDatabase> {
  handle ??= new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;

      if (!db.objectStoreNames.contains(VARIANTS)) {
        const store = db.createObjectStore(VARIANTS, { keyPath: 'id' });
        // Barcode is indexed because a scanner lookup must not scan 3,000 rows; the rest of
        // search runs against an in-memory index built once at load.
        store.createIndex('barcode', 'barcode', { unique: false });
      }

      if (!db.objectStoreNames.contains(META)) db.createObjectStore(META);
    };

    request.onsuccess = () => {
      const db = request.result;
      // Another tab upgrading the schema would otherwise block forever behind this connection.
      db.onversionchange = () => {
        db.close();
        handle = null;
      };
      resolve(db);
    };

    request.onerror = () => {
      handle = null;
      reject(new Error(request.error?.message ?? 'The catalogue store could not be opened.'));
    };
  });

  return handle;
}

const done = (tx: IDBTransaction): Promise<void> =>
  new Promise((resolve, reject) => {
    tx.oncomplete = () => {
      resolve();
    };
    tx.onerror = tx.onabort = () => {
      reject(new Error(tx.error?.message ?? 'The catalogue write failed.'));
    };
  });

const asPromise = <T>(request: IDBRequest<T>): Promise<T> =>
  new Promise((resolve, reject) => {
    request.onsuccess = () => {
      resolve(request.result);
    };
    request.onerror = () => {
      reject(new Error(request.error?.message ?? 'The catalogue read failed.'));
    };
  });

/** Everything, in one read. 3,000 rows lands in roughly 40 ms on a mid-range phone. */
export async function readAllVariants(): Promise<CatalogueVariant[]> {
  const db = await open();
  // IndexedDB is untyped at the boundary — `getAll()` is `IDBRequest<any[]>`. The assertion is
  // the one place that boundary is crossed, and it is crossed exactly once.
  const store = db.transaction(VARIANTS, 'readonly').objectStore(VARIANTS);
  return asPromise(store.getAll() as IDBRequest<CatalogueVariant[]>);
}

/**
 * Applies a sync result. Upserts and deletions in one transaction, so a failure halfway cannot
 * leave the cache holding a variant the server says is gone.
 */
export async function applySync(
  variants: readonly CatalogueVariant[],
  removedIds: readonly VariantId[],
  meta: SyncMeta,
): Promise<void> {
  const db = await open();
  const tx = db.transaction([VARIANTS, META], 'readwrite');
  const store = tx.objectStore(VARIANTS);

  for (const variant of variants) store.put(variant);
  for (const id of removedIds) store.delete(id);

  tx.objectStore(META).put(meta, 'sync');

  await done(tx);
}

/** Wipes and repopulates. Used for a full snapshot and on a shop switch. */
export async function replaceAll(variants: readonly CatalogueVariant[], meta: SyncMeta): Promise<void> {
  const db = await open();
  const tx = db.transaction([VARIANTS, META], 'readwrite');
  const store = tx.objectStore(VARIANTS);

  store.clear();
  for (const variant of variants) store.put(variant);
  tx.objectStore(META).put(meta, 'sync');

  await done(tx);
}

export async function readMeta(): Promise<SyncMeta | null> {
  const db = await open();
  const request = db.transaction(META, 'readonly').objectStore(META).get('sync') as IDBRequest<
    SyncMeta | undefined
  >;
  const value = await asPromise(request);
  return value ?? null;
}

/**
 * Applies a sold quantity locally so the next search shows the new figure without waiting for a
 * sync. Not authoritative — the server owns stock — but a till that still shows "3 in stock"
 * after selling the last one is a till nobody trusts.
 */
export async function adjustCachedQuantity(
  deltas: readonly { variantId: VariantId; quantity: number }[],
): Promise<void> {
  const db = await open();
  const tx = db.transaction(VARIANTS, 'readwrite');
  const store = tx.objectStore(VARIANTS);

  for (const { variantId, quantity } of deltas) {
    const request = store.get(variantId);
    request.onsuccess = () => {
      const variant = request.result as CatalogueVariant | undefined;
      if (variant) store.put({ ...variant, quantity: variant.quantity + quantity });
    };
  }

  await done(tx);
}
